
import { EditOutlined } from "@ant-design/icons";
import {
  ContributeModal,
  DataCard,
  GenericSearchableList,
  openNotification,
  paginationEnum,
  useResponsiveSettings
} from "@seims/shared";
import {
  Button,
  Form,
  List,
} from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { util } from "../../../src/utils/functions/custom";
import { Messages, Notify } from "../../services/custom/Messages";
import { recordError } from "../../utils/logService";
import "./organization-list.css";
import { SelectedOrganizations } from "./SelectedOrganizations";


export const OrganizationList = ({
  formRef,
  onSelectedOrganization,
  onUpdateSelected,
  selectedRowKeys,
  selectedGlobalOrganization,
  setSelectedRowKeys,
  setSelectedGlobalOrganization,
  preselectedOrgs,
  hasUpdated,
  api,
  isEdit,
  organizationBelongsTo = "project",
  mode="add" // add, edit, view
}) => {
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [displaySelectedOrganisations, setDisplaySelectedOrganisations] = useState(false);
  const [orgsContributions, setOrgsContributions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showContribModal, setShowContribModal] = useState(false);
  const [previewOrgsContrib, setPreviewOrgsContrib] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [total, setTotal] = useState(paginationEnum.DEFAULT_MAX_TOTAL);
  const [currentPage, setCurrentPage] = useState(
    paginationEnum.DEFAULT_FIRST_PAGE
  );
  const [pagingDisabled, setPagingDisabled] = useState(false);
  const [localSelectedOrgs, setLocalSelectedOrgs] = useState([]);

  const activeSelectedOrgs = isEdit
    ? selectedGlobalOrganization
    : localSelectedOrgs;

  const selectedOrgsToRender =
    selectedGlobalOrganization && selectedGlobalOrganization.length > 0
      ? selectedGlobalOrganization
      : [];

  const { pageSize } = useResponsiveSettings();
  const [partnersToFetch, setPartnersToFetch] = useState(pageSize);

  const [form] = Form.useForm();
  const searchButtonRef = useRef(null);
  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Type", dataIndex: "type", key: "type" },
    { title: "Location", dataIndex: "address", key: "address" },
  ];

  const getPartnersCallback = useCallback(async () => {
    await getPartners({
        first: (currentPage - 1) * partnersToFetch,
        max: partnersToFetch,
        searchText
      })
  }, [currentPage, pageSize, searchText, partnersToFetch]);

  useEffect(() => {
    getPartnersCallback()
  }, [getPartnersCallback, hasUpdated]);

  const selectedLookup = useMemo(() => {
    return selectedGlobalOrganization.reduce((acc, org) => {
      acc[org.id] = org;
      return acc;
    }, {});
  }, [selectedGlobalOrganization]);

  const getPartners = async ({ first = 0, max = 10, searchText = "" }) => {
    const entity = "partners";
    try {
      setLoading(true);
      const response = await api.getOrganizations({
        first,
        max,
        searchText,
      });

      if (response?.status === 204) {
        setFilteredData([]);
        setPagingDisabled(true);
        setLoading(false);
        return;
      }

      const { results, total } = response.data;

      const mapped = results.map((el) => {
        const existing = selectedLookup[el.id];

        return {
          id: el.id,
          key: el.id.toString(),
          name: el.name,
          canEdit: el.canEdit,

          //  Preserve type when coming back from List
          type:
            existing?.type ??
            el.partnerType?.value ??
            "",

          address: `${el.address?.province} (${el.address?.cityOrTown})`,
          typeid: el.partnerType.key,

          // Preserve contribution data
          contributions: existing?.contributions ?? [],
          otherContribution: existing?.otherContribution ?? null,
        };
      });
console.log("MAPPED PARTNERS", mapped);

      setPartners(mapped);
      setFilteredData(mapped);
      setTotal(total);
      setPagingDisabled(false);
      setLoading(false);
    } catch (error) {
      openNotification(
        "Partners",
        util.parseMessage(Messages.GET_FAILURE, entity),
        Notify.ERROR
      );
      recordError(error);
    }
  };

  const onUpdateContributions = (selected) => {
    let previewContribution = {};
    if (selected) {
      if (selectedGlobalOrganization.length === 0) {
        //LocalOrganization
        previewContribution = orgsContributions.find(
          (c) => c.id === selected.id
        );
      } else {
        //GlobalOrganization
        previewContribution = selectedGlobalOrganization.find(
          (c) => c.id === selected.id
        );
      }

      if (previewContribution) {
        setPreviewOrgsContrib(previewContribution);
      } else {
        setPreviewOrgsContrib(selected);
      }
      onShowContributeModal(true);
    }
  };

  const onRemoveContributions = (selectedId) => {
    let previewContribution = {};

    if (selectedId > 0) {
      if (selectedGlobalOrganization.length === 0) {
        previewContribution = orgsContributions.find(
          (c) => c.id === selectedId
        );
      } else {
        previewContribution = selectedGlobalOrganization.find(
          (c) => c.id === selectedId
        );
      }

      if (previewContribution) {
        setPreviewOrgsContrib([]);
      }

      const itemsToKeep = selectedGlobalOrganization.filter(
        (c) => c.id !== selectedId
      );

      const selectedRows = itemsToKeep.map((c) => c.id.toString());

      setSelectedRowKeys(selectedRows);
      setSelectedRows(itemsToKeep);
      setSelectedGlobalOrganization(itemsToKeep);
      // onSelectedOrganization(itemsToKeep);
    }
  };

  const onPageChange = (page, newPageSize) => {
    setCurrentPage(page);
    setPartnersToFetch(newPageSize);
  };

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
  };

  const handleContributionSave = (partner) => {
    if (!partner?.id) return;

    const normalizedPartner = {
      ...partner,
      type:
        typeof partner.type === "object"
          ? partner.type.value
          : partner.type || "",
    };

    const existing = selectedGlobalOrganization || [];

    const updated = existing.some(p => p.id === normalizedPartner.id)
      ? existing.map(p =>
        p.id === normalizedPartner.id
          ? { ...p, ...normalizedPartner }
          : p
      )
      : [...existing, normalizedPartner];

    // THIS is what actually updates your list
    setSelectedGlobalOrganization(updated);

    // keep UI in sync
    setSelectedRowKeys(
      updated.map(p => p.id.toString())
    );

  };

  const onShowContributeModal = (value) => {
    setShowContribModal(value);

    //? You need to check if the number of selected organizations matches with the checked grid items
    //? If there are more checked items then remove the ones not in the selected list
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) =>
      onSelectChange(selectedRowKeys, selectedRows),
    onSelect: (record, selected, selectedRows) => {
      if (selected) {
        //! PLEASE REVISIT selectedOrganization, must be removed since it's a local state manager and there's a global one
        setSelectedOrganizations([record, ...selectedOrganizations]);
        onUpdateContributions(record);
        onShowContributeModal(true);
        setDisplaySelectedOrganisations(true);
      } else {
        onShowContributeModal(false);
        setDisplaySelectedOrganisations(false);

        let updSelection = [];
        if (selectedRows.length > 0) {
          updSelection = selectedOrganizations.filter(
            (c) => c.id !== record.id
          );
        } else {
          updSelection = [];
          setDisplaySelectedOrganisations(false);
        }
        onRemoveContributions(record.id);
      }
    },
  };

  const toggleSelect = (org) => {
    const isSelected = selectedRowKeys.includes(org.key);

    if (isSelected) {
      // DESELECT
      const newKeys = selectedRowKeys.filter((k) => k !== org.key);
      const newRows = selectedRows.filter((r) => r.id !== org.id);

      setSelectedRowKeys(newKeys);
      setSelectedRows(newRows);

      onRemoveContributions(org.id);
    } else {
      // SELECT
      const newKeys = [...selectedRowKeys, org.key];
      const newRows = [...selectedRows, org];

      setSelectedRowKeys(newKeys);
      setSelectedRows(newRows);

      onUpdateContributions(org);
      setShowContribModal(true);
    }
  };

  const cancelOrgSelection = () => {
    const orgId = selectedRowKeys?.at(selectedRowKeys?.length - 1)
    const org = {id: orgId, key: orgId}
    toggleSelect(org);
  };

  const handleSearch = (text) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const isAddEditMode = ["add", "edit"].includes(mode);  

  return (
    <>
      {/* Selected organizations summary */}
      {selectedOrgsToRender.length > 0 && (
        <SelectedOrganizations
          mode={mode}
          organizationBelongsTo={organizationBelongsTo}
          selectedOrgs={selectedOrgsToRender}
          onShowContributeModal={(org) => {
            setPreviewOrgsContrib(org);
            setShowContribModal(true);
          }}
          onRemoveContr={(orgId) => onRemoveContributions(orgId)}
        />
      )}

      {/* <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h3 style={{ marginBottom: 0 }}>Search and select Partners</h3>
        </Col>
      </Row> */}

      <Form form={form} ref={formRef}>
          <Form.Item name="organization" style={{ margin: 0 }}>
          {(isAddEditMode) && (
            <GenericSearchableList
              isLoading={loading}
              searchHint="Search for an Organisation"
              onHandleSearch={handleSearch}
              dataSource={filteredData}
              pagination={
                pagingDisabled ? {} : {
                  current: currentPage,
                  total: total,
                  onChange: onPageChange
                }
              }
              renderItem={(org, index) => {
                // Your existing multiple-selection logic
                const isSelected = selectedRowKeys.includes(org.key);
                // console.log("ORG", org, isSelected, selectedRowKeys);
                
                // Map the org data to our generic layout
                const cardData = [
                  { label: 'Name', value: org.name },
                  { label: 'Type', value: org.type },
                  { label: 'Location', value: org.address }
                ];

                const rowIndex = (currentPage - 1) * pageSize + index;

                return (
                  <List.Item>
                    <DataCard
                      index={rowIndex}
                      disabled={ !isAddEditMode }
                      isSelected={isSelected}
                      selectionType="multiple" // Uses checkboxes!
                      titleText={org.name}     // Displays the org name
                      onToggleSelect={() => toggleSelect(org)}
                      details={cardData}
                      actions={org?.canEdit === true ? [
                        <Button
                          disabled={!isAddEditMode}
                          key="edit"
                          type="text"
                          icon={<EditOutlined />}
                          style={{
                            backgroundColor: "#0968AF",
                            color: "#fff",
                            borderRadius: "100px",
                            padding: "4px 16px"
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click
                            onUpdateSelected(org);
                          }}
                        >
                          Edit
                        </Button>
                      ] : []}
                    />
                  </List.Item>
                );
              }}
            />
          )}
          </Form.Item>
        </Form>
      {/* </Card> */}

      <ContributeModal
        show={showContribModal}
        onShow={setShowContribModal}
        previewOrgsContrib={previewOrgsContrib}
        selectedOrganizations={selectedGlobalOrganization}
        setOrgsContributions={handleContributionSave}
        organizationBelongsTo={organizationBelongsTo}
        onCancel={cancelOrgSelection}
      />
    </>
  );
};